/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import camelcaseKeys from "camelcase-keys";
import * as v from "valibot";

import type { InstallationSituationId, ObsNetworkId } from "../../enums";

const siteScheme = v.pipe(
    v.looseObject({}),
    v.transform((x) => camelcaseKeys(x, { deep: true })),
    v.object({
        /** 観測点ID */
        siteid2: v.string(),

        /** 観測点運用開始日 */
        startDate: v.nullable(v.string()),

        /** 観測点運用終了日 */
        endDate: v.nullable(v.string()),

        /** 観測点コード */
        siteCode: v.string(),

        /** 観測点名（日本語） */
        siteName: v.string(),

        /** 観測点経度 */
        lon: v.pipe(
            v.number(),
            v.minValue(-180),
            v.maxValue(180)),

        lonStr: v.string(),

        /** 観測点緯度 */
        lat: v.pipe(
            v.number(),
            v.minValue(-90),
            v.maxValue(90)),

        latStr: v.string(),

        /** 観測点標高 */
        elevation: v.number(),

        elevationStr: v.string(),

        /** 観測点センサのGL- */
        sensorDepthGlminus: v.number(),

        sensorDepthGlminusStr: v.string(),

        /** 観測網機関ID */
        obsNetworkId: v.pipe(
            v.number(),
            v.integer(),
            v.minValue(0)),

        /** 設置場所ID */
        installationSituationId: v.pipe(
            v.number(),
            v.integer(),
            v.minValue(0)),

        /** 東北火山フロントからの距離 */
        distVfMf13Nejapan: v.number(),

        distVfMf13NejapanStr: v.string(),

        /** 南西火山フロントからの距離 */
        distVfMf13Swjapan: v.number(),

        distVfMf13SwjapanStr: v.string(),

        /** 表層10m平均S波速度 */
        vs10: v.nullable(v.number()),

        vs10Str: v.nullable(v.string()),

        /** 表層20m平均S波速度 */
        vs20: v.nullable(v.number()),

        vs20Str: v.nullable(v.string()),

        /** 表層30m平均S波速度 */
        vs30: v.nullable(v.number()),

        vs30Str: v.nullable(v.string()),

        /** 250mメッシュコード */
        meshcode250: v.string(),

        /** AVS30 */
        avs30: v.nullable(v.number()),

        avs30Str: v.nullable(v.string()),

        /** 3次メッシュコード */
        meshcode3: v.string(),

        /** VS1100上面深さ */
        d1100: v.nullable(v.number()),

        d1100Str: v.nullable(v.string()),

        /** VS1400上面深さ */
        d1400: v.nullable(v.number()),

        d1400Str: v.nullable(v.string()),

        /** VS1700上面深さ */
        d1700: v.nullable(v.number()),

        d1700Str: v.nullable(v.string()),

        /** VS2100上面深さ */
        d2100: v.nullable(v.number()),

        d2100Str: v.nullable(v.string()),

        /** 地震学的基盤面深さ */
        dbase: v.nullable(v.number()),

        dbaseStr: v.nullable(v.string()),
    }),
    v.transform((x) => {
        return {
            ...x,
            startDate: (x.startDate?.startsWith("9999"))? null : x.startDate,
            endDate: (x.endDate?.startsWith("9999"))? null : x.endDate,
            obsNetworkId: x.obsNetworkId as ObsNetworkId,
            installationSituationId: x.installationSituationId as InstallationSituationId,
        };
    }));

export type Site = v.InferOutput<typeof siteScheme>;

/**
 * 与えられたデータを観測点データとしてパースする。
 *
 * @param data 対象のデータ
 * @returns パース結果
 */
export const parseSite = (data: unknown) => {
    return v.parse(siteScheme, data);
};

/**
 * 与えられたデータを観測点データの配列としてパースする。
 *
 * @param data 対象のデータ
 * @returns パース結果
 */
export const parseSites = (data: unknown) => {
    if (Array.isArray(data)) {
        return data.map((x) => parseSite(x));
    } else {
        throw new TypeError("data is not an array.");
    }
};
